import { useState, useMemo, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@store/authStore';
import {
  RiFileList3Line,
  RiDownloadLine,
  RiMoneyDollarCircleLine,
  RiLineChartLine,
  RiTeamLine,
  RiPercentLine,
  RiFilterLine,
  RiExpandUpDownFill,
  RiArrowUpDownLine
} from '@remixicon/react';
import { Card, CardContent } from '@components/ui/Card';
import { Checkbox } from '@components/ui/Checkbox';
import { Skeleton } from '@components/ui/Skeleton';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, SCOPES } from '@/lib/permissions';
import { Tabs, TabsList, TabsTrigger } from '@components/ui/Tabs';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuCheckboxItem,
} from '@components/ui/DropdownMenu';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@components/ui/Table';
import { formatPrice } from '@/utils';
import { QuoteDetailsDrawer } from '@/features/quotes/components';
import { getQuoteStatusConfig } from '@/features/quotes/utils';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { formatUTCToDateTime } from '@/utils';

const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Date (newest first)' },
  { value: 'date_asc', label: 'Date (oldest first)' },
  { value: 'taxes_desc', label: 'Total Taxes (high to low)' },
  { value: 'taxes_asc', label: 'Total Taxes (low to high)' },
  { value: 'client_asc', label: 'Client Name (A–Z)' },
];

// Preset → columnSort mapping for the global "Sort by" dropdown. Keeping the
// dropdown in lockstep with the per-header sort state means there's one source
// of truth for ordering.
const SORT_PRESETS = {
  date_desc: { key: 'departure_date', dir: 'desc' },
  date_asc: { key: 'departure_date', dir: 'asc' },
  taxes_desc: { key: 'total_taxes', dir: 'desc' },
  taxes_asc: { key: 'total_taxes', dir: 'asc' },
  client_asc: { key: 'client_name', dir: 'asc' },
};

// One source of truth for column rendering, sort, and filter type. The order
// here drives the header row, the body cells, and the CSV export.
const COLUMNS = [
  { key: 'quote_number', label: 'Trip ID', type: 'text' },
  { key: 'client_name', label: 'Client Name', type: 'text' },
  { key: 'date_quote_sent', label: 'Date Quote Sent', type: 'text', align: 'right' },
  { key: 'departure_date', label: 'Next Flight Date', type: 'date' },
  { key: 'route', label: 'Route', type: 'text' },
  { key: 'trip_legs', label: 'Trip Legs', type: 'text' },
  { key: 'operator_name', label: 'Operator Name', type: 'text' },
  { key: 'tail_number', label: 'Tail Number', type: 'text' },
  { key: 'broker_name', label: 'Broker Name', type: 'text', align: 'right' },
  { key: 'passenger_segments', label: 'Passengers', type: 'number', align: 'right' },
  { key: 'operator_cost', label: 'Operator Cost', type: 'number', align: 'right' },
  { key: 'broker_margin', label: 'Broker Margin', type: 'number', align: 'right' },
  { key: 'broker_margin_percent', label: 'Broker Margin (%)', type: 'number', align: 'right' },
  { key: 'total_taxable_amount', label: 'Total Taxable Amount', type: 'number', align: 'right' },
  { key: 'federal_excise_tax', label: 'FET on Trip', type: 'number', align: 'right' },
  { key: 'segment_tax', label: 'Total Segment Fee', type: 'number', align: 'right' },
  { key: 'card_fee', label: 'Card Fee', type: 'number', align: 'right' },
  { key: 'total_taxes', label: 'Total Taxes', type: 'number', align: 'right' },
  { key: 'total_due', label: 'Total Amount', type: 'number', align: 'right' },
  { key: 'date_paid', label: 'Date Paid', type: 'text', align: 'right' },
  { key: 'quote_status', label: 'Quote Status', type: 'text', align: 'right' },
  { key: 'payment_status', label: 'Payment Status', type: 'text', align: 'right' }
];

const isFilterActive = (filter) => {
  if (!filter) return false;
  if (filter.type === 'text') return !!filter.value;
  if (filter.type === 'number') return (filter.min !== '' && filter.min != null) || (filter.max !== '' && filter.max != null);
  if (filter.type === 'date') return !!filter.start || !!filter.end;
  return false;
};

const matchesFilter = (val, filter) => {
  if (!isFilterActive(filter)) return true;
  if (filter.type === 'text') {
    return String(val ?? '').toLowerCase().includes(filter.value.toLowerCase());
  }
  if (filter.type === 'number') {
    const n = Number(val) || 0;
    if (filter.min !== '' && filter.min != null && n < Number(filter.min)) return false;
    if (filter.max !== '' && filter.max != null && n > Number(filter.max)) return false;
    return true;
  }
  if (filter.type === 'date') {
    const d = new Date(val).getTime();
    if (Number.isNaN(d)) return false;
    if (filter.start && d < new Date(filter.start).getTime()) return false;
    if (filter.end && d > new Date(filter.end).getTime() + 86_400_000 - 1) return false;
    return true;
  }
  return true;
};

const FilterInputs = ({ col, filter, onChange }) => {
  const stop = (e) => e.stopPropagation();
  if (col.type === 'text') {
    return (
      <div className="flex flex-col gap-2" onKeyDown={stop}>
        <label className="text-xs font-medium text-gray-600">Contains</label>
        <input
          type="text"
          value={filter?.value || ''}
          onChange={(e) => onChange({ type: 'text', value: e.target.value })}
          className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          autoFocus
        />
        {isFilterActive(filter) && (
          <button type="button" onClick={() => onChange(null)} className="text-xs text-gray-500 hover:text-gray-700 text-left">Clear</button>
        )}
      </div>
    );
  }
  if (col.type === 'number') {
    return (
      <div className="flex flex-col gap-2" onKeyDown={stop}>
        <label className="text-xs font-medium text-gray-600">Range</label>
        <div className="flex gap-2">
          <input
            type="number"
            placeholder="Min"
            value={filter?.min ?? ''}
            onChange={(e) => onChange({ type: 'number', min: e.target.value, max: filter?.max ?? '' })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <input
            type="number"
            placeholder="Max"
            value={filter?.max ?? ''}
            onChange={(e) => onChange({ type: 'number', min: filter?.min ?? '', max: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        {isFilterActive(filter) && (
          <button type="button" onClick={() => onChange(null)} className="text-xs text-gray-500 hover:text-gray-700 text-left">Clear</button>
        )}
      </div>
    );
  }
  if (col.type === 'date') {
    return (
      <div className="flex flex-col gap-2" onKeyDown={stop}>
        <label className="text-xs font-medium text-gray-600">Date range</label>
        <input
          type="date"
          value={filter?.start || ''}
          onChange={(e) => onChange({ type: 'date', start: e.target.value, end: filter?.end || '' })}
          className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <input
          type="date"
          value={filter?.end || ''}
          onChange={(e) => onChange({ type: 'date', start: filter?.start || '', end: e.target.value })}
          className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        {isFilterActive(filter) && (
          <button type="button" onClick={() => onChange(null)} className="text-xs text-gray-500 hover:text-gray-700 text-left">Clear</button>
        )}
      </div>
    );
  }
  return null;
};

const ColumnHeader = ({ col, sort, onSort, filter, onFilterChange }) => {
  const isSorted = sort?.key === col.key;
  const SortIcon = RiExpandUpDownFill;
  const filterOn = isFilterActive(filter);
  return (
    <div className="flex items-center gap-1 justify-between">
      <button
        type="button"
        onClick={() => onSort(col.key)}
        className="hover:text-gray-900 focus:outline-none truncate"
      >
        {col.label}
      </button>
      <div className="flex items-center gap-1 shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={`p-1 rounded hover:bg-gray-200 ${filterOn ? 'text-primary-600' : 'text-gray-400'}`}
              aria-label={`Filter ${col.label}`}
            >
              <RiFilterLine className="w-3.5 h-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60 p-3">
            <FilterInputs col={col} filter={filter} onChange={onFilterChange} />
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          onClick={() => onSort(col.key)}
          className="p-1 rounded hover:bg-gray-200 focus:outline-none"
          aria-label={`Sort ${col.label}`}
        >
          <SortIcon className={`w-3.5 h-3.5 ${isSorted ? 'text-gray-700' : 'text-gray-400'}`} />
        </button>
      </div>
    </div>
  );
};

// Statuses considered real revenue for tax reporting. Kept in a constant so the
// fetch query and the Filter popover stay in lockstep. The API's `status` filter
// is a single-enum, so we fan out one request per status and merge.
const REPORTABLE_STATUSES = [
  'ACCEPTED',
  'PASSENGERS_PENDING',
  'WAITING_FLIGHT',
  'IN_TRANSIT',
  'COMPLETED',
  'DIVERTED',
];

const toTitleCase = (s) =>
  String(s)
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const compactCurrency = (value) => {
  const num = Number(value) || 0;
  const abs = Math.abs(num);
  if (abs >= 1_000_000) return `$${(num / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `$${(num / 1_000).toFixed(abs >= 100_000 ? 0 : 1)}k`;
  return `$${num.toFixed(0)}`;
};

const KpiCard = ({ label, value, subtitle, Icon, iconBg, iconColor, valueSize = 'text-2xl' }) => (
  <Card className="h-full !rounded-3xl">
    <CardContent className="p-4 h-full">
      <div className="flex flex-col h-full">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col min-w-0">
            {subtitle && <span className="text-xs font-medium text-gray-500">{label}</span>}
            <span className={`${valueSize} font-semibold text-gray-900 ${subtitle ? 'mt-1' : ''} truncate`}>{value}</span>
            {subtitle && <span className="text-xs text-gray-500 mt-1">{subtitle}</span>}
          </div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${iconBg}`}>
            <Icon className={`w-4 h-4 ${iconColor}`} />
          </div>
        </div>
        {!subtitle && (
          <span className="text-xs font-medium text-gray-500 mt-auto pt-3">{label}</span>
        )}
      </div>
    </CardContent>
  </Card>
);

/**
 * TaxReports Page
 * Displays tax information from quotes in a datatable format.
 */
const TaxReports = () => {

  const { userProfile, isLoading: isAuthLoading, isAuthenticated, fetchUserProfile } = useAuthStore();

  const [timeWindow, setTimeWindow] = useState('all');
  const [columnSort, setColumnSort] = useState({ key: 'departure_date', dir: 'desc' });
  const [columnFilters, setColumnFilters] = useState({});
  const [statusFilter, setStatusFilter] = useState(() => new Set(REPORTABLE_STATUSES));
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [drawerQuoteId, setDrawerQuoteId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !userProfile && !isAuthLoading) {
      fetchUserProfile();
    }
  }, [isAuthenticated, userProfile, isAuthLoading, fetchUserProfile]);

  const profileId = userProfile?.userprofile_id;
  const orgId = userProfile?.org_id;

  // Per-role data scope from the permission matrix (Financials module).
  // self -> created_by: profileId (broker/junior_broker see only their own)
  // global -> broker_org_id: orgId (admin/finance see the full org)
  const { scope: getScope } = usePermissions();
  const financialsScope = getScope(MODULES.FINANCIALS);
  const scopeFilter = useMemo(() => {
    if (financialsScope === SCOPES.GLOBAL && orgId) return { broker_org_id: orgId };
    if (financialsScope === SCOPES.SELF && profileId) return { created_by: profileId };
    return null;
  }, [financialsScope, orgId, profileId]);

  const { data: quotesData, isLoading, error } = useQuery({
    queryKey: ['tax-reports', financialsScope, profileId, orgId],
    enabled: !!scopeFilter,
    queryFn: async () => {
      const filters = {
        ...scopeFilter,
        status: REPORTABLE_STATUSES.join(','),
        // Default include from apiClient + payment for date_paid / payment status.
        include: 'bid_items,bid_items.bid_options,bid_items.bid_options.aircraft,bid_items.bid_options.operator,client,organizations,payments',
      };
      const data = await apiClient.getQuotes(0, 100, filters);

      return data.map(quote => {
        const bid = quote.bid_items?.[0]?.bid;
        const allBidOptions = bid?.bid_options || [];
        const bidOptions = allBidOptions.filter(option => option.status === 'ACCEPTED');
        const primaryOption = bidOptions[0] || allBidOptions[0];

        const totalOperatorCost = quote.operator_cost || 0;
        const totalBrokerMargin = quote.broker_margin || 0;
        const totalFET = quote.federal_excise_tax || 0;
        const totalSegmentTax = quote.segment_tax || 0;

        const client = quote.client;
        const clientName = [client?.first_name, client?.last_name].filter(Boolean).join(' ') || 'N/A';

        const trip_legs = bid?.leg_itinerary?.length ?? 0;
        const trips = bid?.leg_itinerary || [];

        const depart = trips[0]?.departure_airport;
        const arrive = trips[0]?.arrival_airport;
        const route = depart && arrive ? `${depart} → ${arrive}` : (depart || arrive || 'N/A');

        const totalTaxes = parseFloat(totalFET) + parseFloat(totalSegmentTax);
        const taxableAmount = parseFloat(totalOperatorCost + totalBrokerMargin) || 0;
        

        return {
          quote_id: quote.quote_id,
          quote_number: quote.quote_number,
          client_name: clientName,
          date_quote_sent: quote.created_at,
          departure_date: formatUTCToDateTime(trips[0]?.departure_date, getAirportTimezone(trips[0]?.departure_airport), "MMM D"),
          route,
          trip_legs,
          operator_name: primaryOption?.operator?.name || '-',
          tail_number: primaryOption?.aircraft?.tail_number || '-',
          broker_name: quote.created_by_name || '-',
          operator_cost: totalOperatorCost,
          broker_margin: totalBrokerMargin,
          broker_margin_percent: totalOperatorCost > 0 ? (totalBrokerMargin / totalOperatorCost) * 100 : 0,
          total_taxable_amount: taxableAmount,
          federal_excise_tax: totalFET,
          passenger_segments: quote.passenger_count || 0,
          segment_tax: totalSegmentTax,
          card_fee: quote.card_fee || 0,
          total_taxes: totalTaxes,
          total_due: quote.total_due || 0,
          date_paid: quote.payments?.status=="CAPTURED" ? quote.payments?.created_at : '',
          quote_status: quote.status,
          payment_status: quote.payments?.status=="CAPTURED" ? "Paid" : (quote.payments?.status ? toTitleCase(quote.payments.status) : '-'),
        };
      });
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Apply time-window tab (All / Monthly / Yearly) on departure_date, then
  // the Filter popover's status selection on top.
  const filteredData = useMemo(() => {
    if (!quotesData) return [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const activeColumnFilters = Object.entries(columnFilters).filter(([, f]) => isFilterActive(f));
    const globalNeedle = globalFilter.trim().toLowerCase();

    return quotesData.filter(quote => {
      if (!statusFilter.has(quote.quote_status)) return false;

      // Time-window tab on departure_date.
      if (timeWindow !== 'all') {
        if (!quote.departure_date) return false;
        const qd = new Date(quote.departure_date);
        if (Number.isNaN(qd.getTime())) return false;
        if (timeWindow === 'monthly') {
          if (qd.getFullYear() !== currentYear || qd.getMonth() !== currentMonth) return false;
        } else if (timeWindow === 'yearly') {
          if (qd.getFullYear() !== currentYear) return false;
        }
      }

      // Per-column header filters.
      for (const [key, f] of activeColumnFilters) {
        if (!matchesFilter(quote[key], f)) return false;
      }

      // Global search: match against any column's value (string form).
      if (globalNeedle) {
        const hit = COLUMNS.some(c => String(quote[c.key] ?? '').toLowerCase().includes(globalNeedle));
        if (!hit) return false;
      }
      return true;
    });
  }, [quotesData, timeWindow, statusFilter, columnFilters, globalFilter]);

  const sortedData = useMemo(() => {
    const arr = [...filteredData];
    if (!columnSort?.key) return arr;
    const col = COLUMNS.find(c => c.key === columnSort.key);
    if (!col) return arr;
    const sign = columnSort.dir === 'desc' ? -1 : 1;
    arr.sort((a, b) => {
      const av = a[columnSort.key];
      const bv = b[columnSort.key];
      if (col.type === 'number') return ((Number(av) || 0) - (Number(bv) || 0)) * sign;
      if (col.type === 'date') {
        const ad = new Date(av || 0).getTime() || 0;
        const bd = new Date(bv || 0).getTime() || 0;
        return (ad - bd) * sign;
      }
      return String(av || '').localeCompare(String(bv || '')) * sign;
    });
    return arr;
  }, [filteredData, columnSort]);

  // Derive the "Sort by" dropdown's current value from columnSort so the
  // global dropdown and the per-header sort never disagree.
  const sortByValue = useMemo(() => {
    const found = Object.entries(SORT_PRESETS).find(
      ([, v]) => v.key === columnSort.key && v.dir === columnSort.dir,
    );
    return found?.[0] || '';
  }, [columnSort]);

  const handleColumnSort = (key) => {
    setColumnSort(prev => {
      if (prev?.key === key) return { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
      const col = COLUMNS.find(c => c.key === key);
      // Numbers and dates default to descending (most-recent / largest first),
      // text defaults to ascending (A–Z).
      const defaultDir = col?.type === 'text' ? 'asc' : 'desc';
      return { key, dir: defaultDir };
    });
  };

  const handleSortByChange = (val) => {
    const preset = SORT_PRESETS[val];
    if (preset) setColumnSort(preset);
  };

  const setColumnFilter = (key, value) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      if (value == null) delete next[key]; else next[key] = value;
      return next;
    });
  };

  const toggleRowSelection = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const visibleSelectedCount = useMemo(
    () => sortedData.reduce((n, q) => n + (selectedIds.has(q.quote_id) ? 1 : 0), 0),
    [sortedData, selectedIds],
  );
  const allVisibleSelected = sortedData.length > 0 && visibleSelectedCount === sortedData.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  const toggleSelectAllVisible = () => {
    setSelectedIds(prev => {
      if (allVisibleSelected) {
        // Deselect only the currently visible rows; preserve selections that
        // fell outside the current filter window.
        const next = new Set(prev);
        sortedData.forEach(q => next.delete(q.quote_id));
        return next;
      }
      const next = new Set(prev);
      sortedData.forEach(q => next.add(q.quote_id));
      return next;
    });
  };

  const totals = useMemo(() => {
    const empty = {
      totalOperatorCost: 0, totalBrokerMargin: 0, totalTaxable: 0, totalFET: 0,
      totalSegments: 0, totalSegmentFee: 0, totalTaxes: 0, totalCardFee: 0, totalDue: 0,
    };
    if (!sortedData.length) return empty;
    return sortedData.reduce((acc, quote) => ({
      totalOperatorCost: acc.totalOperatorCost + quote.operator_cost,
      totalBrokerMargin: acc.totalBrokerMargin + quote.broker_margin,
      totalTaxable: acc.totalTaxable + quote.total_taxable_amount,
      totalFET: acc.totalFET + quote.federal_excise_tax,
      totalSegments: acc.totalSegments + quote.passenger_segments,
      totalSegmentFee: acc.totalSegmentFee + quote.segment_tax,
      totalTaxes: acc.totalTaxes + quote.total_taxes,
      totalCardFee: acc.totalCardFee + quote.card_fee,
      totalDue: acc.totalDue + quote.total_due,
    }), empty);
  }, [sortedData]);

  // KPIs reflect the time-window / status filter (not the sort).
  const kpis = useMemo(() => {
    const totalRevenue = filteredData.reduce((s, q) => s + (q.total_due || 0), 0);
    const totalCommissions = filteredData.reduce((s, q) => s + (q.broker_margin || 0), 0);

    const clientTotals = filteredData.reduce((acc, q) => {
      const name = q.client_name || 'Unknown';
      acc[name] = (acc[name] || 0) + (q.total_due || 0);
      return acc;
    }, {});
    const topClient = Object.entries(clientTotals).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

    // "Closed" = COMPLETED, "sent" = every quote in this reportable window.
    // Approximation: DRAFT/SENT aren't fetched here.
    const closed = filteredData.filter(q => q.quote_status === 'COMPLETED').length;
    const sent = filteredData.length;
    const ratio = sent ? Math.round((closed / sent) * 100) : 0;

    return { totalRevenue, totalCommissions, topClient, ratio, closed, sent };
  }, [filteredData]);

  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const year = date.getFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  };

  const toggleStatus = (status) => {
    setStatusFilter(prev => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status); else next.add(status);
      return next;
    });
  };

  const handleDownloadCSV = () => {
    // If any rows are selected, export only those (in current sort order).
    // Otherwise fall back to exporting everything currently visible.
    const exportRows = selectedIds.size > 0
      ? sortedData.filter(q => selectedIds.has(q.quote_id))
      : sortedData;
    if (!exportRows.length) return;

    const csvText = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

    // date_quote_sent / date_paid are typed as 'text' in COLUMNS but hold ISO
    // dates — render them like the table does.
    const DATE_KEYS = new Set(['departure_date', 'date_quote_sent', 'date_paid']);
    const INTEGER_KEYS = new Set(['passenger_segments']);
    const SUMMABLE_NUMBER_KEYS = COLUMNS
      .filter(c => c.type === 'number' && c.key !== 'broker_margin_percent')
      .map(c => c.key);

    const formatCell = (q, col) => {
      const v = q[col.key];
      if (DATE_KEYS.has(col.key)) return csvText(v ? formatDisplayDate(v) : '');
      if (col.key === 'broker_margin_percent') return csvText(`${(Number(v) || 0).toFixed(2)}%`);
      if (INTEGER_KEYS.has(col.key)) return Number(v) || 0;
      if (col.type === 'number') return (Number(v) || 0).toFixed(2);
      return csvText(v);
    };

    const headers = COLUMNS.map(c => csvText(c.label));
    const rows = exportRows.map(q => COLUMNS.map(c => formatCell(q, c)));

    // Totals reflect exactly what was exported (selected subset or full visible).
    const totalsByKey = SUMMABLE_NUMBER_KEYS.reduce((acc, key) => {
      acc[key] = exportRows.reduce((s, q) => s + (Number(q[key]) || 0), 0);
      return acc;
    }, {});
    const totalsRow = COLUMNS.map((col, i) => {
      if (i === 0) return csvText('TOTALS');
      if (!(col.key in totalsByKey)) return csvText('');
      if (INTEGER_KEYS.has(col.key)) return totalsByKey[col.key];
      return totalsByKey[col.key].toFixed(2);
    });
    rows.push(totalsRow);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `tax-report-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="pb-20 bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">
      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
        <KpiCard
          label="Total Revenue"
          value={compactCurrency(kpis.totalRevenue)}
          Icon={RiMoneyDollarCircleLine}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
        />
        <KpiCard
          label="Total Commissions"
          value={compactCurrency(kpis.totalCommissions)}
          Icon={RiLineChartLine}
          iconBg="bg-green-100"
          iconColor="text-green-500"
        />
        <KpiCard
          label="Top Client"
          value={kpis.topClient}
          subtitle="Highest booking volume"
          valueSize="text-lg"
          Icon={RiTeamLine}
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
        />
        <KpiCard
          label="Quote-to-Closed Trip Ratio"
          value={`${kpis.ratio}%`}
          subtitle={`${kpis.closed} closed trips / ${kpis.sent} quotes sent`}
          Icon={RiPercentLine}
          iconBg="bg-orange-50"
          iconColor="text-orange-600"
        />
      </div>

      {/* Toolbar */}
      <Card className="mb-2 !bg-transparent !border-0 !shadow-none">
        <CardContent className="p-4 !px-0">
          <div className="flex flex-wrap items-center gap-3">
            <Tabs value={timeWindow} onValueChange={setTimeWindow} className="!bg-gray-100 !rounded-lg">
              <TabsList className="!h-auto gap-2">
                <TabsTrigger value="all" className="!px-[40px] text-gray-400 !rounded-lg data-[state=active]:!bg-white data-[state=active]:text-gray-700">All</TabsTrigger>
                <TabsTrigger value="monthly" className="!px-[30px] text-gray-400 !rounded-lg data-[state=active]:!bg-white data-[state=active]:text-gray-700">Monthly</TabsTrigger>
                <TabsTrigger value="yearly" className="!px-[30px] text-gray-400 !rounded-lg data-[state=active]:!bg-white data-[state=active]:text-gray-700">Yearly</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex-1" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-full text-sm hover:bg-gray-50 transition-colors flex items-center gap-2">
                  <RiFilterLine className="w-4 h-4" />
                  Filter
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Search all columns</DropdownMenuLabel>
                <div className="px-2 pb-2" onKeyDown={(e) => e.stopPropagation()}>
                  <input
                    type="text"
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder="Type to search…"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                    autoFocus
                  />
                  {globalFilter && (
                    <button
                      type="button"
                      onClick={() => setGlobalFilter('')}
                      className="mt-1 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Status</DropdownMenuLabel>
                {REPORTABLE_STATUSES.map(status => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={statusFilter.has(status)}
                    onCheckedChange={() => toggleStatus(status)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {status.replace(/_/g, ' ')}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors flex items-center gap-2">
                  <RiArrowUpDownLine className="w-4 h-4" />
                  Sort by
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup value={sortByValue} onValueChange={handleSortByChange}>
                  {SORT_OPTIONS.map(opt => (
                    <DropdownMenuRadioItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>

            {financialsScope === SCOPES.GLOBAL && (
              <button
                onClick={handleDownloadCSV}
                disabled={!sortedData.length}
                className="px-4 py-2 bg-primary-500 text-white rounded-full text-sm hover:bg-green-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RiDownloadLine className="w-4 h-4" />
                {selectedIds.size > 0 ? `Export ${selectedIds.size} Selected` : 'Export to CSV'}
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <RiFileList3Line className="w-10 h-10 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Couldn't load tax data</h3>
              <p className="text-sm text-gray-500">{error.message || 'Please try again.'}</p>
            </div>
          ) : sortedData?.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <RiFileList3Line className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No tax data found</h3>
              <p className="text-sm text-gray-500">Tax information will appear here once you have quotes.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-100">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allVisibleSelected ? true : (someVisibleSelected ? 'indeterminate' : false)}
                        onCheckedChange={toggleSelectAllVisible}
                        aria-label="Select all visible rows"
                        className="!bg-white"
                      />
                    </TableHead>
                    {COLUMNS.map(col => (
                      <TableHead key={col.key} className={`!text-gray-600 !font-normal ${col.align === 'right' ? 'text-right' : ''}`}>
                        <ColumnHeader
                          col={col}
                          sort={columnSort}
                          onSort={handleColumnSort}
                          filter={columnFilters[col.key]}
                          onFilterChange={(val) => setColumnFilter(col.key, val)}
                        />
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.map((quote) => (
                    <TableRow key={quote.quote_id} data-state={selectedIds.has(quote.quote_id) ? 'selected' : undefined}>
                      <TableCell className="w-10">
                        <Checkbox
                          checked={selectedIds.has(quote.quote_id)}
                          onCheckedChange={() => toggleRowSelection(quote.quote_id)}
                          aria-label={`Select ${quote.quote_number}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        <button
                          type="button"
                          onClick={() => {
                            setDrawerQuoteId(quote.quote_id);
                            setIsDrawerOpen(true);
                          }}
                          className="text-primary-600 hover:text-primary-700 hover:underline focus:outline-none"
                        >
                          {quote.quote_number}
                        </button>
                      </TableCell>
                      <TableCell>{quote.client_name}</TableCell>
                      <TableCell>{formatDisplayDate(quote.date_quote_sent)}</TableCell>   
                      <TableCell>{formatDisplayDate(quote.departure_date)}</TableCell>
                      
                      <TableCell>{quote.route}</TableCell>
                      <TableCell>{quote.trip_legs}</TableCell>

                      <TableCell>{quote.operator_name}</TableCell>
                      <TableCell>{quote.tail_number}</TableCell>
                      <TableCell>{quote.broker_name}</TableCell>

                      <TableCell>{quote.passenger_segments}</TableCell>

                      <TableCell>{formatPrice(quote.operator_cost)}</TableCell>
                      <TableCell>{formatPrice(quote.broker_margin)}</TableCell>
                      <TableCell>{quote.broker_margin_percent}%</TableCell>

                      <TableCell>{formatPrice(quote.total_taxable_amount)}</TableCell>
                      <TableCell>{formatPrice(quote.federal_excise_tax)}</TableCell>
                      <TableCell>{formatPrice(quote.segment_tax)}</TableCell>
                      <TableCell>{formatPrice(quote.card_fee)}</TableCell>
                      <TableCell>{formatPrice(quote.total_taxes)}</TableCell>
                      <TableCell>${quote.total_due.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 10 })}</TableCell>

                      <TableCell>{quote.date_paid ? formatDisplayDate(quote.date_paid) : '-'}</TableCell>
                    
                      <TableCell>
                        {(() => {
                          const config = getQuoteStatusConfig(quote.quote_status);
                          const Icon = config.icon;
                          return (
                            <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md ${config.className}`}>
                              {Icon && <Icon className="w-3.5 h-3.5" />}
                              {config.label}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {quote.payment_status === '-' ? (
                          '-'
                        ) : (
                          <span
                            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md ${
                              quote.payment_status === 'Paid'
                                ? 'bg-green-150 text-green-450'
                                : 'bg-amber-50 text-amber-600'
                            }`}
                          >
                            {quote.payment_status}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* <TableRow className="bg-gray-100 font-semibold">
                    <TableCell colSpan={7}>Totals</TableCell>
                    <TableCell>{totals.totalSegments}</TableCell>
                    <TableCell colSpan={3}></TableCell>
                    <TableCell>{formatPrice(totals.totalOperatorCost)}</TableCell>
                    <TableCell>{formatPrice(totals.totalBrokerMargin)}</TableCell>
                    <TableCell>{formatPrice(totals.totalTaxable)}</TableCell>
                    <TableCell>{formatPrice(totals.totalFET)}</TableCell>
                    <TableCell>{formatPrice(totals.totalSegmentFee)}</TableCell>
                    <TableCell>{formatPrice(totals.totalCardFee)}</TableCell>
                    <TableCell>{formatPrice(totals.totalTaxes)}</TableCell>
                    <TableCell>{formatPrice(totals.totalDue)}</TableCell>
                    <TableCell colSpan={6}></TableCell>
                  </TableRow> */}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <QuoteDetailsDrawer
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        quoteId={drawerQuoteId}
      />
    </div>
  );
};

export default TaxReports;